import { EpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { IEpticaRequestFrame } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { TransferHistorySelectionStatus } from '@agent/core/contact/transfer/models/transfer';
import { TransferDeprecatedService } from '@agent/core/contact/transfer/transfer-deprecated.service';
import { Component, Inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NG1_EPTICA, NG1_TRANSFER } from '@commons/core/core.provider.ajs';
import { IEpticaExctractAttachment, IEpticaExctractEvent } from '@commons/history/history-mail/models/eptica-extract';
import { EpticaSelector } from '@commons/history/history-mail/models/eptica-selector';
import { IPromise, isDefined } from 'angular';
import { Subscription } from 'rxjs';

import { BookingFacade, isNotNullOrUndefined, Logger, Memo, MomentService, Option } from '@contact/upgrade';

interface InternalEvent {
  evt: IEpticaExctractEvent;
  disabled: boolean;
  open: boolean;
  conversational: boolean;
  allAttachment: IEpticaExctractAttachment[];
  index: number;
  isHtmlFormat: boolean;
  selected: boolean;
  safeContentHtml: SafeHtml;
}

@Component({
  selector: 'history-mail-for-transfer',
  templateUrl: './history-mail-for-transfer.component.html',
  styleUrls: ['./history-mail-for-transfer.component.scss'],
})
export class HistoryMailForTransferComponent implements OnInit, OnChanges, OnDestroy {
  public internalEvents: InternalEvent[];
  public selectedMailCounter = 0;
  public selectionEnabled: TransferHistorySelectionStatus;

  @Input() epticaEvents: IEpticaExctractEvent[];
  @Input() memo: Memo;
  @Input() responseId: string;
  private requestPromise: IPromise<IEpticaRequestFrame>;
  private sub: Subscription[] = [];
  private requestId: number;

  constructor(
    private readonly logger: Logger,
    private readonly timeUtil: MomentService,
    private readonly domSanitizer: DomSanitizer,
    @Inject(NG1_EPTICA) public epticaDeprecatedService: EpticaDeprecatedService,
    @Inject(NG1_TRANSFER) public transferDeprecatedService: TransferDeprecatedService,
    private readonly contactFacade: BookingFacade
  ) {
    this.logger = this.logger.forSource('HistoryMailForTransferComponent');
  }

  ngOnInit() {
    this.sub.push(
      this.contactFacade
        .getById(this.responseId)
        .pipe(isNotNullOrUndefined())
        .subscribe(contact => {
          this.requestId = contact.epticaRequestId || 0;
          this.requestPromise = this.epticaDeprecatedService.loadRequest({
            requestId: this.requestId,
            responseId: this.responseId,
          });
        })
    );
    this.sub.push(
      this.transferDeprecatedService.isHistorySelectionEnabled().subscribe(isSelectable => {
        this.selectionEnabled = isSelectable;
        if (this.selectionEnabled === TransferHistorySelectionStatus.DISABLED) {
          this.unSelectAllMails();
        } else {
          this.selectAllMails();
        }
      })
    );
  }

  ngOnChanges() {
    this.internalEvents = Option.of(this.epticaEvents)
      .map(events =>
        events
          .sort((a, b) => b.date.getTime() - a.date.getTime())
          .map((evt, index) => ({
            evt: evt,
            disabled: this.isDisabled(evt),
            open: index === 0,
            conversational: HistoryMailForTransferComponent.isConversational(evt),
            allAttachment: HistoryMailForTransferComponent.allAttachment(evt),
            index: index,
            isHtmlFormat: !evt.isPlainTextFormat,
            selected: false,
            safeContentHtml: this.getTrustedHtml(evt.contentHtml),
          }))
      )
      .getOrElse([]);
  }

  ngOnDestroy() {
    this.sub.forEach(subscription => subscription.unsubscribe());
  }

  getTrustedHtml(contentHtml: string): SafeHtml {
    return this.domSanitizer.bypassSecurityTrustHtml(contentHtml);
  }

  extractReadableDate(dateToConvert: Date): string {
    return this.timeUtil.readableDateShort(dateToConvert);
  }

  extractReadableTime(dateToConvert: Date): string {
    return this.timeUtil.readableTime(dateToConvert);
  }

  getHeaderDate(event: IEpticaExctractEvent) {
    return `${this.extractReadableDate(event.date)} ${this.extractReadableTime(event.date)}`;
  }

  isEmpty(value: string | null | undefined): boolean {
    return Option.of(value)
      .filter(v => v !== '')
      .isEmpty();
  }

  isIncommingMessage(event: IEpticaExctractEvent): boolean {
    return (
      event.selector === EpticaSelector.request_event_type_client_message ||
      event.selector === EpticaSelector.request_event_type_opening ||
      event.selector === EpticaSelector.request_event_type_monitoring
    );
  }

  clickOnSwitch(event: InternalEvent) {
    event.isHtmlFormat = !event.isHtmlFormat;
    // click on Eptica's switch linked to have the same result when printing
    if (this.requestPromise) {
      this.requestPromise.then(request => {
        request.switchCommentFormat(event.evt.contentId);
      });
    }
  }

  clickOnAttachment(mouseEvent: MouseEvent, event: InternalEvent) {
    if (event.open) {
      mouseEvent.stopPropagation();
    }
  }

  selectAllMails() {
    this.internalEvents.forEach(internalEvent => {
      if (!internalEvent.selected) {
        internalEvent.selected = true;
        this.checkMailHistoryInEptica(internalEvent);
        this.transferDeprecatedService.checkMailHistory([this.requestId], internalEvent.evt.contentId);
      }
    });
    this.selectedMailCounter = this.internalEvents.length;
    if (this.selectionEnabled === TransferHistorySelectionStatus.NONE_OR_ALL) {
      this.transferDeprecatedService.toggleMailHistoryBlindOption([this.requestId], false);
    }
  }

  unSelectAllMails() {
    this.internalEvents.forEach(internalEvent => {
      if (internalEvent.selected) {
        internalEvent.selected = false;
        this.checkMailHistoryInEptica(internalEvent);
        this.transferDeprecatedService.uncheckMailHistory([this.requestId], internalEvent.evt.contentId);
      }
    });
    this.selectedMailCounter = 0;
    if (this.selectionEnabled === TransferHistorySelectionStatus.NONE_OR_ALL) {
      this.transferDeprecatedService.toggleMailHistoryBlindOption([this.requestId], true);
    }
  }

  toggleMailHistorySelection(internalEvent: InternalEvent) {
    internalEvent.selected = !internalEvent.selected;
    this.checkMailHistoryInEptica(internalEvent);
    this.transferDeprecatedService.changeMailHistory([this.requestId], internalEvent.evt.contentId, internalEvent.selected);
    this.selectedMailCounter = this.internalEvents.filter(event => event.selected).length;
  }

  private static allAttachment(event: IEpticaExctractEvent): IEpticaExctractAttachment[] {
    if (!event) {
      return [];
    } else {
      return event.attachment.concat(
        Option.of(event.response)
          .map(r => r.attachment)
          .getOrElse([])
      );
    }
  }

  private isDisabled(event: IEpticaExctractEvent): boolean {
    if (HistoryMailForTransferComponent.isConversational(event)) {
      return false;
    } else {
      return (
        this.isEmpty(event.comment) &&
        this.isEmpty(event.content) &&
        (!isDefined(event.response) ||
          (isDefined(event.response) && this.isEmpty(event.response?.comment) && this.isEmpty(event.response?.content)))
      );
    }
  }

  static isConversational(event: IEpticaExctractEvent): boolean {
    switch (event.selector) {
      case EpticaSelector.request_event_type_response:
      case EpticaSelector.request_event_type_outgoing_mail:
      case EpticaSelector.request_event_type_client_message:
      case EpticaSelector.request_event_type_monitoring:
        return true;
      // Eptica used the same 'request_event_type_opening' event for the reoping by agent or client
      // we only want to display the reoping by client
      // in the agent case there is no message
      // so we test the message content to determinate the isConversational
      case EpticaSelector.request_event_type_opening:
        return !!event.recipient || !!event.ccRecipient || !!event.subject || !!event.content;
      default:
        return false;
    }
  }

  private checkMailHistoryInEptica(internalEvent: InternalEvent) {
    if (this.requestPromise) {
      this.requestPromise.then(request => {
        request.toggleMailHistoryCheckBox(internalEvent.evt.contentId, internalEvent.selected);
      });
    }
  }
}
